import * as React from "react";
import {useEffect, useState} from "react";
import {ErrorComponent} from "../base/Error.component";
import {hasConsultantLevelAccess, hasPortfolioPageAccess} from "../../utils/sessionUtil";
import {connect, useDispatch, useSelector} from "react-redux";
import {
    currentPlan,
    hasClientModelPortfolioAccess,
    hasClientPortfolioAccess,
    hasPortfolioAccess,
} from "../../../mainReducerMapSelectors";
import {NoAccessNotification} from "../base/header/NoAccessNotification";
import {IApplicationRootState} from "../../../applicationState";
import {AssetAllocationTab} from "./AssetAllocationTab";
import {FinancialReconciliationTab} from "./FinancialReconciliationTab";
import {IAccountRollingPerformance, IPortfolioFeesTreeNode, PortfolioApi} from "../../api/PortfolioApi";
import {IPlanTreeNode} from "../../model/Portfolio.model";
import {LoadingSpinner} from "../icons/LoadingSpinner.component";
import {PerformanceTab} from "./PerformanceTab";
import {portfolioPageCurrentTab} from "./PortfolioMapSelectors";
import {setCurrentTabAction} from "./PortfolioActions";
import {StyledToggle} from "../common/material-ui-core/Toggle.component";
import {IPlanInfo} from "../../model/ClientsAndPlans.model";
import planApi from "../../api/PlanApi";
import {requestPlanSettings} from "../root/rootActions";
import {
    hasAccessToModelPortfolioPage,
    hasValidModelPortfolioData, isDcPlan,
    planHasAccessToModelPortfolioPage
} from "./portfolioUtil";
import {FeesTab} from "./FeesTab";
import {PortfolioMethodology} from "./PortfolioMethodology";
import {AnyThunkDispatch} from "../../../commonTypes";
import {UserBehaviorTrackerApi} from "../../api/UserBehaviorTrackerApi";
import {getBrowserLocationPath} from "../../api/BrowserWrapper";

export enum PortfolioTab {
    ASSET_ALLOCATION = "Asset Allocation",
    FINANCIAL_RECONCILIATION = "Financial Reconciliation",
    PERFORMANCE = "Performance",
    FEES = "Fees"
}

export interface IPortfolioPageProps {
    portfolioAccess: boolean;
    clientPortfolioAccess: boolean;
    clientModelPortfolioAccess: boolean;
    currentPlan: IPlanInfo;
}

export const CLIENT_MODEL_PORTFOLIO_NOTIFICATION_MESSAGE = "The Model Portfolio page is currently hidden from client users of this plan.";

export const PortfolioPage: React.FunctionComponent<IPortfolioPageProps> = (props) => {
    const currentTab = useSelector((state: IApplicationRootState) => portfolioPageCurrentTab(state));
    const [planTreeNodes, setPlanTreeNodes] = useState<IPlanTreeNode[]>([]);
    const [clientPortfolioAccess, setClientPortfolioAccess] = useState<boolean>(props.clientPortfolioAccess);
    const [loading, setLoading] = useState<boolean>(true);
    const [portfolioFees, setPortfolioFees] = useState<IPortfolioFeesTreeNode[]>([]);
    const [planType, setPlanType] = useState<string>("");
    const [hasValidModelPortfolioDataState, setHasValidModelPortfolioDataState] = useState<boolean>(false);
    const [accountRollingPerformance, setAccountRollingPerformance] = useState<IAccountRollingPerformance[]>([]);

    const dispatch: AnyThunkDispatch = useDispatch();

    useEffect(() => {
        if(hasPortfolioPageAccess(props.portfolioAccess, props.clientPortfolioAccess)) {
            if (!currentTab) {
                dispatch(setCurrentTabAction(PortfolioTab.ASSET_ALLOCATION));
            }

            PortfolioApi.getCurrentPlanPortfolioTree()
                .then((response) => {
                    setPlanTreeNodes(response);
                    setLoading(false);
                })
                .catch(() => {
                    setLoading(false);
                });

            PortfolioApi.getPortfolioFees()
                .then((response) => setPortfolioFees(response));

            PortfolioApi.getCurrentPlanType().then((planType: string) => {
                setPlanType(planType);
                if (isDcPlan(planType)) {
                    PortfolioApi.getRollingPerformance()
                        .then(response => {
                            setAccountRollingPerformance(response);
                        });
                }
            });
        }
    }, []);

    useEffect(() => {
        const fetchModelPortfolioData = async () => {
            try {
                const performanceMetricsResponse = await PortfolioApi.getPerformanceMetrics();
                if (performanceMetricsResponse) {
                    const portfolioComparisonResponse = await PortfolioApi.getCurrentPlanPortfolioComparison();
                    if(hasValidModelPortfolioData(performanceMetricsResponse, portfolioComparisonResponse)) {
                        setHasValidModelPortfolioDataState(true);
                    }
                }
            } catch {
                setHasValidModelPortfolioDataState(false);
            }
        };

        if (hasAccessToModelPortfolioPage(planType, props.clientModelPortfolioAccess)) {
            fetchModelPortfolioData();
        }
    }, [planType]);

    useEffect(() => {
        if(currentTab) {
            UserBehaviorTrackerApi.trackUserBehavior(getBrowserLocationPath() + "/" + currentTab);
        }
    }, [currentTab]);

    const renderClientAccessNotification = () => {
        return (hasConsultantLevelAccess() && !props.clientPortfolioAccess)
            ? <div className="portfolio-page__notification" data-testid={"portfolio-page__notification"}>
                <NoAccessNotification
                    message={"The Portfolio page is currently hidden from client users of this plan."}/>
            </div>
            : null;
    };

    const renderClientModelPortfolioAccessNotification = () => {
        return (hasValidModelPortfolioDataState && planHasAccessToModelPortfolioPage(planType)
            && hasConsultantLevelAccess() && !props.clientModelPortfolioAccess)
            ? <div className="portfolio-page__notification">
                <NoAccessNotification
                    message={CLIENT_MODEL_PORTFOLIO_NOTIFICATION_MESSAGE}/>
            </div>
            : null;
    };

    const getSelected = (tab: PortfolioTab) => tab === currentTab ? "selected" : "";

    const renderTabs = () => {
        return <div className="tabs__container">
            {renderTab(PortfolioTab.ASSET_ALLOCATION)}
            {renderTab(PortfolioTab.FINANCIAL_RECONCILIATION)}
            {renderTab(PortfolioTab.PERFORMANCE)}
            {portfolioFees.length > 0 && renderTab(PortfolioTab.FEES)}
        </div>;
    };

    const renderTab = (tab: PortfolioTab) => {
        return <div className={`tab clickable ${getSelected(tab)}`}
                    onClick={() => {
                        dispatch(setCurrentTabAction(tab));
                    }}
                    data-testid="portfolio-page__tab">
            <h6>{tab}</h6>
        </div>;
    };

    const renderAssetAllocationTab = () => {
        return currentTab === PortfolioTab.ASSET_ALLOCATION
            && <AssetAllocationTab
                planTreeNodes={planTreeNodes}
                planType={planType}
                showModelPortfolioComparison ={hasValidModelPortfolioDataState}/>;
    };
    const renderFinancialReconciliationTab = () => {
        return currentTab === PortfolioTab.FINANCIAL_RECONCILIATION && <FinancialReconciliationTab/>;
    };

    const renderPerformanceTab = () => {
        return currentTab === PortfolioTab.PERFORMANCE
            && <PerformanceTab
                planTreeNodes={planTreeNodes}
                planType={planType}
                accountRollingPerformance={accountRollingPerformance}/>;
    };

    const renderFeesTab = () => {
        return currentTab === PortfolioTab.FEES &&  <FeesTab portfolioFees={portfolioFees}/>;
    };
    const updateClientPortfolioAccess = (clientPortfolioAccess: boolean) => {
        setClientPortfolioAccess(clientPortfolioAccess);
        planApi.updatePlanClientPortfolioEnabled(props.currentPlan.id, clientPortfolioAccess)
            .then(() => {
                dispatch(requestPlanSettings());
            });
    };

    const renderTitleAndPortfolioSwitch = () => {
      return <div className="portfolio-page__title-and-portfolio-switch">
          <div className="portfolio-page__title-and-switch-container">
              <h1>Portfolio Overview</h1>
              {
                  hasConsultantLevelAccess()
                  ?
                  <div className="portfolio-page__switch-label" data-testid="portfolio-page__switch-label">
                      <StyledToggle
                        toggleState={clientPortfolioAccess}
                        setToggle={updateClientPortfolioAccess}
                        label={"Show Portfolio for Client Users"}
                        labelPlacement="start"/>
                  </div>
                  : null
              }
          </div>
          <PortfolioMethodology/>
      </div>;
    };

    const renderContent = () =>
        <div className="main-content new-common-styles portfolio__page-container"
             data-testid="portfolio__page-container">
            {renderClientAccessNotification()}
            {renderClientModelPortfolioAccessNotification()}
            {renderTitleAndPortfolioSwitch()}
            {renderTabs()}
            {renderAssetAllocationTab()}
            {renderFinancialReconciliationTab()}
            {renderPerformanceTab()}
            {renderFeesTab()}
        </div>;

    return hasPortfolioPageAccess(props.portfolioAccess, props.clientPortfolioAccess)
        ? loading ? <LoadingSpinner/> : renderContent()
        : <ErrorComponent/>;
};

export const mapStateToProps = (state: IApplicationRootState): IPortfolioPageProps => {
    return {
        portfolioAccess: hasPortfolioAccess(state),
        clientPortfolioAccess: hasClientPortfolioAccess(state),
        clientModelPortfolioAccess: hasClientModelPortfolioAccess(state),
        currentPlan: currentPlan(state),
    };
};

const connectedComponent = connect<IPortfolioPageProps>(mapStateToProps)(PortfolioPage);

export default connectedComponent;